import { apiFetch } from './client.js';

// #399 — the SERVER-side half of "my settings": email notification toggles.
// The device-local half (theme, density, formats, defaults) never leaves the
// browser and lives in src/lib/prefs.ts.
//
// The event vocabulary is duplicated here rather than imported, because the
// SPA and the API share no package: apps/spa cannot reach into apps/api. The
// API is the authority — its PUT schema is `additionalProperties: false` over
// exactly these keys, so a key that drifts out of step here is a 400, not a
// silent no-op. That is the intended failure mode.
export const NOTIFICATION_EVENTS = [
  'caseAssigned',
  'caseStatusChanged',
  'caseDecision',
  'disputeUpdate',
  'slaDeadline',
  'settlementSync',
  'intakeSubmission',
] as const;

export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

// MIRROR of NOTIFICATION_EVENT_LABELS in apps/api/src/lib/notification-prefs.ts,
// which is canonical. Tied to it by a disk-read pin rather than by this comment:
// pages/Preferences.producers.test.ts, "NOTIFICATION_EVENT_LABELS matches the
// API copy, read out of the API source". The portal's own labels are NOT this
// list — apps/portal/src/api/preferences.ts rewrites them for a customer
// reader, deliberately.
export const NOTIFICATION_EVENT_LABELS: Record<NotificationEvent, string> = {
  caseAssigned: 'A case is assigned to me',
  caseStatusChanged: 'A case I am on changes status',
  caseDecision: 'A vetting decision is recorded',
  disputeUpdate: 'An IP or SHAFT dispute is updated',
  slaDeadline: 'An SLA or cure deadline is approaching or has passed',
  settlementSync: 'A Settlement Platform sync fails or completes',
  intakeSubmission: 'A new application is submitted',
};

export type EmailPrefs = Record<NotificationEvent, boolean>;

export interface PreferencesPayload {
  emailPrefs: EmailPrefs;
}

export async function fetchPreferences(): Promise<PreferencesPayload> {
  return apiFetch<PreferencesPayload>('/me/preferences');
}

/**
 * Full replacement, matching the API: the form always knows the complete
 * desired state, and a partial write would make "turn this back on"
 * indistinguishable from "leave this one alone".
 */
export async function savePreferences(emailPrefs: EmailPrefs): Promise<PreferencesPayload> {
  return apiFetch<PreferencesPayload>('/me/preferences', {
    method: 'PUT',
    body: JSON.stringify({ emailPrefs }),
  });
}

export interface SignInEvent {
  /** ISO instant. */
  time: string;
  /** LOGIN or LOGIN_ERROR. */
  type: string;
  clientId: string | null;
  ipAddress: string | null;
  /** Keycloak's failure reason on LOGIN_ERROR; null on a success. */
  error: string | null;
}

/**
 * Recent sign-ins for the signed-in user.
 *
 * Answers 503 (an ApiProblem, with an explanatory `detail`) until an operator
 * grants bcid-admin-svc the view-events realm-management role. Callers must
 * render that detail rather than an empty list: "no sign-ins recorded" and
 * "we are not allowed to look" are very different statements to make to
 * someone checking their account for intrusions.
 */
export async function fetchSignInHistory(limit = 20): Promise<{ items: SignInEvent[] }> {
  return apiFetch<{ items: SignInEvent[] }>(`/me/sign-in-history?limit=${limit}`);
}

export interface ActivityEntry {
  id: string;
  timestamp: string;
  caseId: string;
  registrant: string | null;
  action: string;
  detail: Record<string, unknown>;
}

export async function fetchMyActivity(limit = 20): Promise<{ items: ActivityEntry[] }> {
  return apiFetch<{ items: ActivityEntry[] }>(`/me/activity?limit=${limit}`);
}
